import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Camera, Upload, X, Loader2 } from "lucide-react";

export const AuthImg = ({ path, className, alt = "" }) => {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let url;
    if (!path) return;
    setErr(false);
    api.get(`/files/${path}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setErr(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (err) return <div className={`${className} bg-gray-200 flex items-center justify-center text-xs text-gray-500`}>—</div>;
  if (!src) return <div className={`${className} bg-gray-100 flex items-center justify-center`}><Loader2 className="h-5 w-5 text-gray-400 animate-spin" /></div>;
  return <img src={src} alt={alt} className={className} />;
};

export const PhotoUpload = ({ value, onChange, testId = "photo-upload", label = "Photo" }) => {
  const [uploading, setUploading] = useState(false);
  const ref = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.path);
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">{label}</label>
      {value ? (
        <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
          <AuthImg path={value} className="w-full max-h-48 object-cover" />
          <button
            type="button"
            data-testid={`${testId}-remove`}
            onClick={() => onChange(null)}
            className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/70 text-white flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            data-testid={`${testId}-camera`}
            onClick={() => ref.current?.click()}
            disabled={uploading}
            className="h-14 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-xl tap-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Camera className="h-4 w-4 mr-2" />}
            Take photo
          </Button>
          <Button
            type="button"
            data-testid={`${testId}-file`}
            onClick={() => ref.current?.click()}
            disabled={uploading}
            variant="outline"
            className="h-14 rounded-xl tap-btn"
          >
            <Upload className="h-4 w-4 mr-2" /> Upload
          </Button>
        </div>
      )}
      <input
        ref={ref}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
};
